import { SubmissionStatus } from '@bp/bp-graphql-types';
import { EmptyState, SchoolYearIllustration, ShareAlternateIcon, UpdateIcon } from '@bp/ui-components';
import { BpActionButton, BpCard } from 'components/BpCard/BpCard';
import { FileListDownloader } from 'components/Files/components/FileList/FileListDownloader';
import { useSubmissions } from 'components/Submissions/hooks/useSubmissions';
import { getSubmissionStatusTranslation } from 'components/Submissions/utils/submissionUtils';
import { TipTapTextExtender } from 'components/TipTap/TipTapTextExtender';
import { useRequireParams } from 'hooks/useRequireParams';
import { useTranslation } from 'react-i18next';
import styles from './SubmissionAnswerCard.module.scss';

type SubmissionAnswerCardTeacherProps = {
  submissionUuid: string;
  onRequestPublication: () => void;
  onReopen: () => void;
};

export const SubmissionAnswerCardTeacher = ({
  submissionUuid,
  onRequestPublication,
  onReopen,
}: SubmissionAnswerCardTeacherProps) => {
  const { t } = useTranslation();
  const { courseUuid, assignmentUuid } = useRequireParams(['courseUuid', 'assignmentUuid']);

  const { submissions } = useSubmissions({ courseUuid, assignmentUuid, submissionUuid });

  const submission = submissions.find((s) => s.uuid === submissionUuid);
  if (!submission) throw new Error('Submission not found');

  const status = submission.status;
  const material = submission.material;
  const owner = submission.owner;
  const isPublic = submission.public;

  return (
    <BpCard
      noPadding={material.fileEntries.length > 0}
      alwaysCollapsible
      className={styles['submission-answer-card-teacher']}
      header={{
        headline: t('common.answer'),
        subHeadline: isPublic ? t('submissions.publicised') : undefined,
        showSubHeadlineAsChip: true,
        actions: [
          ...(!(
            status === SubmissionStatus.Draft ||
            status === SubmissionStatus.Feedback ||
            status === SubmissionStatus.Todo
          ) && !isPublic
            ? [
                {
                  text: t('submissions.requestToPublicise'),
                  callback: () => onRequestPublication(),
                  icon: <ShareAlternateIcon />,
                  hierarchy: 'tertiary',
                } as BpActionButton,
              ]
            : []),
          ...(status !== SubmissionStatus.Todo &&
          status !== SubmissionStatus.Draft &&
          status !== SubmissionStatus.Feedback
            ? [
                {
                  text: getSubmissionStatusTranslation(SubmissionStatus.Feedback, 'teacher'),
                  callback: () => onReopen(),
                  icon: <UpdateIcon />,
                  hierarchy: 'tertiary',
                } as BpActionButton,
              ]
            : []),
        ],
      }}
    >
      {status !== SubmissionStatus.Todo && status !== SubmissionStatus.Draft ? (
        <>
          {!material.text ? (
            <EmptyState hideIcon subtitle={t('submissions.noAnswerText')} padding='l' />
          ) : (
            <TipTapTextExtender
              uuid={material.uuid}
              content={material.text ?? ''}
              padding={material.fileEntries.length === 0 ? 'none' : 'default'}
            />
          )}
          {material.fileEntries && material.fileEntries.length > 0 && (
            <FileListDownloader files={material.fileEntries} className='mt-7' />
          )}
        </>
      ) : (
        <div className={styles['no-answer']}>
          <SchoolYearIllustration className={styles.icon} />
          <div className={styles.hint}>
            <div className={styles.text}>
              {t('submissions.empty', {
                name: owner.name,
              })}
            </div>
          </div>
        </div>
      )}
    </BpCard>
  );
};
